<script setup>
import { isNil } from 'lodash'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import SSOApiHandler from '@/api/sso'
import { DIALOG_TYPES } from '@/utils/components-configurations/app-dialog'
import { SAML_CONNECTION_ENTITY_KEYS, SAML_DOMAIN_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { objectOrNullProp } from '@/utils/prop-validators'
import { SAML_CONNECTION_DOMAIN_STATUS_IDS } from '@/utils/sso-connection-settings'
import { SUPPORT_EMAIL, SUPPORT_EMAIL_LINK } from '@/utils/support'
import { uid } from '@/utils/uid'

import AppDialog from '@/components/AppDialog'
import AppHrefLink from '@/components/AppHrefLink'
import DomainsList from '@/components/integrations/sso/DomainsList'

defineOptions({
  name: 'ActivateConnectionModal'
})

const props = defineProps({
  connection: {
    validator: v => objectOrNullProp(v),
    required: true
  }
})

const showModal = defineModel('show', {
  type: Boolean
})

const emit = defineEmits({
  'reload-connections': null
})

const closeModal = () => {
  showModal.value = false
  emit('reload-connections')
}

const loading = ref(false)

const normalizedDomains = computed(() => {
  return props.connection.domains.map(d => {
    return {
      ...d,
      [SAML_DOMAIN_ENTITY_KEYS.ID]: uid()
    }
  })
})

const isAllDomainsValidated = computed(() => {
  if (isNil(props.connection)) return false

  return normalizedDomains.value.every(
    d => d[SAML_DOMAIN_ENTITY_KEYS.STATUS_ID] === SAML_CONNECTION_DOMAIN_STATUS_IDS.VERIFIED
  )
})

const api = new SSOApiHandler()

const onConfirm = async () => {
  if (!isAllDomainsValidated.value) return

  loading.value = true

  try {
    await api.activateSamlConnection(props.connection[SAML_CONNECTION_ENTITY_KEYS.ID])

    closeModal()
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}

const { t } = useI18n()
</script>

<template>
  <AppDialog
    :loading-confirm-button="loading"
    :loading="loading"
    :disable-confirm-button="!isAllDomainsValidated"
    :show="showModal"
    :type="DIALOG_TYPES.INFO"
    hide-hero
    :title="t('integration_settings.table_header_domains')"
    @on-close="closeModal"
    @on-confirm="onConfirm"
  >
    <div class="acm-Content">
      <DomainsList :model-value="normalizedDomains" :loading="loading" readonly>
        <template #message>
          <i18n-t scope="global" keypath="sso.activate_connection.message">
            <template #break>
              <br />
            </template>
            <template #email>
              <AppHrefLink :href="SUPPORT_EMAIL_LINK" :target="null">
                {{ SUPPORT_EMAIL }}
              </AppHrefLink>
            </template>
          </i18n-t>
        </template>
      </DomainsList>
    </div>

    <template #confirm-btn-text> {{ t('action.activate') }} </template>
  </AppDialog>
</template>

<style scoped lang="scss"></style>
